/** Steps out of shadow trees as well, so widgets nested in one are covered */
const composedParentOf = (element: HTMLElement): HTMLElement | null => {
    if (element.parentElement) return element.parentElement;

    const root = element.getRootNode();
    return root instanceof ShadowRoot ? root.host as HTMLElement : null;
};

const isHiddenByStyles = (element: HTMLElement): boolean => {
    for (let node: HTMLElement | null = element; node; node = composedParentOf(node)) {
        const {display, visibility} = getComputedStyle(node);
        if (display === 'none' || visibility === 'hidden') return true;
    }

    return false;
};

/**
 * Whether a checkout rendered inside this widget instance would actually reach
 * the visitor. Host pages duplicate widgets — a theme's mobile menu copy, say —
 * and a copy in hidden markup renders the checkout where nobody can see it.
 *
 * Callers must leave the click alone when this is false: a link that opens in a
 * new tab beats one that silently does nothing.
 *
 * @param hostElement the custom element the widget renders into, if it has one
 */
export const canShowCheckout = (hostElement?: HTMLElement | null): boolean => {
    if (!hostElement) return true; // Not mounted as a custom element: nothing to judge it by
    if (!hostElement.isConnected) return false;

    // checkVisibility settles hidden ancestors and shadow boundaries in one go;
    // where it is missing (Safari below 17.4) the same walk is done by hand
    return hostElement.checkVisibility?.({ checkVisibilityCSS: true })
        ?? !isHiddenByStyles(hostElement);
};
